import { useQuery } from '@tanstack/react-query';
import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { Input, Label } from '@/components/Forms';
import InfoTooltip from '@/components/InfoTooltip';
import Tag from '@/components/Tag';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import { VideoTopicResource } from '@/types/resources';
import { api } from '@/utils/api';
import { TopicsLevel } from '@/utils/enums';

import { FormData } from './AttachmentModal';

const InputWrap = styled.div`
  position: relative;
  ${Input} {
    padding-right: 65px;
  }
`;

const Capitalize = styled.span`
  text-transform: capitalize;
`;

const SuggestionsHeading = styled(Text)`
  margin-bottom: 16px;
`;

const LowerCase = styled.span`
  text-transform: lowercase;
`;

export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Autocomplete = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const AutocompleteButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const TeamSuggestions = styled.div`
  margin-top: 24px;
`;

const AttachmentBox = styled.div<{
  $isGray?: boolean;
}>`
  ${({ $isGray }) =>
    $isGray &&
    css`
      margin: 32px 0;
    `}
`;

const StyledTag = styled(Text)`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  white-space: nowrap;
  border-radius: 50px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  transition: border-color ease-out 0.2s;
  background-color: ${({ theme }) => theme.colors.white};
  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

interface Props {
  control: Control<FormData>;
  setValue: UseFormSetValue<FormData>;
  videoId: number;
  suggestionsEndpoint: string;
  placeholder?: string;
  category: string;
  level?: TopicsLevel;
  label?: string;
  hasSuggestions?: boolean;
}

const AttachmentsField: FC<Props> = ({
  control,
  setValue,
  videoId,
  suggestionsEndpoint,
  placeholder,
  category,
  level = TopicsLevel.GENRE,
  label,
  hasSuggestions = true,
}) => {
  const [suggestions, setSuggestions] = useState<VideoTopicResource[]>([]);

  const [title, setTitle] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const attachments = useWatch({
    control,
    name: 'attachments',
  });

  const debouncedTopic = useDebounce(title, 400);

  const filteredAattachments = attachments?.filter((t) => t.level === level);

  useEffect(() => {
    if (debouncedTopic.length < 2) {
      return setSuggestions([]);
    }

    api
      .get<never, VideoTopicResource[]>(suggestionsEndpoint, {
        params: {
          search: debouncedTopic,
          except_for_video: videoId,
          ...(category === 'script' && { level }),
        },
      })
      .then((suggestions) => setSuggestions(suggestions));
  }, [debouncedTopic, videoId, suggestionsEndpoint, category, level]);

  const addAttachment = (topicToAdd: string) => {
    if (!topicToAdd) return;
    if (
      !attachments.find(
        (a) =>
          a.title ===
          attachments.find((topic) => topic.title === topicToAdd)?.title,
      )
    ) {
      setValue('attachments', [
        ...attachments,
        { title: topicToAdd, level: level },
      ]);
    }

    setTitle('');
    setSuggestions([]);
  };

  const onAttachmentRemove = (topicToRemove: string) => {
    setValue(
      'attachments',
      attachments.filter((t) => t.title !== topicToRemove),
    );
  };

  const splitMatchedText = (str: string) => {
    const regExp = new RegExp(debouncedTopic, 'gi');
    return str.replace(regExp, (match) => `<span>${match}</span>`);
  };

  const onSuggestionClick = (suggestion: string) => {
    addAttachment(suggestion);
    inputRef.current?.focus();
  };

  const { data: teamSuggestions } = useQuery<VideoTopicResource[]>(
    ['topics_suggestions', { category, level }],
    () => {
      return api.get<never, VideoTopicResource[]>(
        '/v1/video-topics/suggested',
        {
          params: {
            category,
            ...(category === 'script' && { level }),
          },
        },
      );
    },
    {
      refetchOnMount: false,
      staleTime: 120 * 1000,
    },
  );

  const filteredTeamSuggestions = teamSuggestions?.filter(
    (s) => !attachments.map((a) => a.title).includes(s.title),
  );

  const filteredSuggestions = suggestions.filter(
    (s) => !attachments.map((a) => a.title).includes(s.title),
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    addAttachment(title);
  };

  return (
    <AttachmentBox $isGray={category === 'script' && true}>
      <InputWrap>
        <Label>
          {!!label ? (
            `${label} ${category !== 'type' && category}`
          ) : (
            <Capitalize>{category !== 'type' && category}</Capitalize>
          )}
          {category === 'type' ? 'Video types' : ' topics'}
          {!!label && (
            <InfoTooltip
              title={`${label} ${category} topics`}
              content="-"
              position="bottom-center"
            />
          )}
        </Label>
        <Input
          placeholder={placeholder}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          ref={inputRef}
          onKeyDown={onKeyDown}
        />
        {!!filteredSuggestions.length && (
          <Autocomplete>
            {filteredSuggestions.map((s) => (
              <AutocompleteButton
                key={s.title}
                type="button"
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onSuggestionClick(s.title);
                }}
              >
                <Text
                  $color="gray"
                  $type="captionSemibold"
                  dangerouslySetInnerHTML={{
                    __html: splitMatchedText(s.title),
                  }}
                />
                <Text $color="gray" $type="extraSmall">
                  {!!s.team && `created by ${s.team.name}`}
                </Text>
              </AutocompleteButton>
            ))}
          </Autocomplete>
        )}
      </InputWrap>
      {!!filteredAattachments?.length && (
        <Tags>
          {filteredAattachments.map((t) => (
            <Tag
              key={t.title}
              onDelete={(e) => {
                e.stopPropagation();
                onAttachmentRemove(t.title);
              }}
            >
              {t.title}
            </Tag>
          ))}
        </Tags>
      )}
      {!!filteredTeamSuggestions?.length && hasSuggestions && (
        <TeamSuggestions>
          <SuggestionsHeading $type="smallBold">
            Suggested {!!label && <LowerCase>{label}</LowerCase>}
            {category === 'type' ? ' video types' : ` ${category} topics`}
          </SuggestionsHeading>
          <Tags>
            {filteredTeamSuggestions.map((suggestion) => (
              <StyledTag
                type="button"
                as="button"
                $type="small"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  addAttachment(suggestion.title);
                }}
                key={suggestion.title}
              >
                {suggestion.title}
              </StyledTag>
            ))}
          </Tags>
        </TeamSuggestions>
      )}
    </AttachmentBox>
  );
};

export default AttachmentsField;
